import type {
  CreateWorkflowInput,
  PatchWorkflowInput,
  WorkflowDetail,
  WorkflowDraftInput,
  WorkflowRunDetail,
  WorkflowRunSegment,
  WorkflowRunView,
  WorkflowScheduleFireView,
  WorkflowSettings,
  WorkflowSummary,
} from '@ofmos/shared/workflow';
import type { WorkflowClient } from '../client';
import type { Transport } from './transport';

/**
 * `WorkflowClient` over `/api/workflows` (workflow-builder.md D45).
 *
 * Thin by design, like the webhook and pending clients: every rule that matters - who may
 * build one, whether a draft is legal, whether a manual run applies to the record somebody
 * picked, whether activation can register a cron - is enforced on the SERVER, and a second
 * opinion here would only ever be a stale one. The screens surface the server's refusal
 * rather than predicting it, which is also what makes every one of those refusals a
 * sentence a person reads instead of a disabled button they cannot explain.
 */
export class HttpWorkflowClient implements WorkflowClient {
  constructor(private readonly transport: Transport) {}

  async list(): Promise<readonly WorkflowSummary[]> {
    const response = await this.transport.request<{ workflows: readonly WorkflowSummary[] }>({
      method: 'GET',
      path: '/api/workflows',
    });
    return response.workflows;
  }

  async get(id: string): Promise<WorkflowDetail> {
    const response = await this.transport.request<{ workflow: WorkflowDetail }>({
      method: 'GET',
      path: `/api/workflows/${id}`,
    });
    return response.workflow;
  }

  async create(input: CreateWorkflowInput): Promise<WorkflowDetail> {
    const response = await this.transport.request<{ workflow: WorkflowDetail }>({
      method: 'POST',
      path: '/api/workflows',
      body: input,
    });
    return response.workflow;
  }

  async update(id: string, patch: PatchWorkflowInput): Promise<WorkflowDetail> {
    const response = await this.transport.request<{ workflow: WorkflowDetail }>({
      method: 'PATCH',
      path: `/api/workflows/${id}`,
      body: patch,
    });
    return response.workflow;
  }

  async remove(id: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/workflows/${id}` });
  }

  async saveDraft(id: string, draft: WorkflowDraftInput): Promise<WorkflowDetail> {
    const response = await this.transport.request<{ workflow: WorkflowDetail }>({
      method: 'PUT',
      path: `/api/workflows/${id}/draft`,
      body: draft,
    });
    return response.workflow;
  }

  async activate(id: string): Promise<WorkflowDetail> {
    const response = await this.transport.request<{ workflow: WorkflowDetail }>({
      method: 'POST',
      path: `/api/workflows/${id}/activate`,
      body: {},
    });
    return response.workflow;
  }

  async run(id: string, recordId: string): Promise<WorkflowRunView> {
    const response = await this.transport.request<{ run: WorkflowRunView }>({
      method: 'POST',
      path: `/api/workflows/${id}/run`,
      body: { recordId },
    });
    return response.run;
  }

  async runs(
    id: string,
    filter: { readonly segment?: WorkflowRunSegment; readonly limit?: number } = {},
  ): Promise<{ readonly runs: readonly WorkflowRunView[]; readonly nextCursor: string | null }> {
    return this.transport.request({
      method: 'GET',
      path: `/api/workflows/${id}/runs`,
      query: {
        // `all` is the server's own default; sending it would be a segment in the URL that
        // says nothing, and the key it is part of would then have two spellings.
        ...(filter.segment === undefined || filter.segment === 'all'
          ? {}
          : { segment: filter.segment }),
        limit: filter.limit ?? 25,
      },
    });
  }

  async fires(
    id: string,
    filter: { readonly limit?: number } = {},
  ): Promise<{
    readonly fires: readonly WorkflowScheduleFireView[];
    readonly nextCursor: string | null;
  }> {
    return this.transport.request({
      method: 'GET',
      path: `/api/workflows/${id}/fires`,
      query: { limit: filter.limit ?? 10 },
    });
  }

  async getRun(runId: string): Promise<WorkflowRunDetail> {
    const response = await this.transport.request<{ run: WorkflowRunDetail }>({
      method: 'GET',
      path: `/api/workflow-runs/${runId}`,
    });
    return response.run;
  }

  async rerun(runId: string): Promise<WorkflowRunView> {
    const response = await this.transport.request<{ run: WorkflowRunView }>({
      method: 'POST',
      path: `/api/workflow-runs/${runId}/rerun`,
      body: {},
    });
    return response.run;
  }

  async settings(): Promise<WorkflowSettings> {
    const response = await this.transport.request<{ settings: WorkflowSettings }>({
      method: 'GET',
      path: '/api/workflow-settings',
    });
    return response.settings;
  }

  async setSettings(paused: boolean): Promise<WorkflowSettings> {
    const response = await this.transport.request<{ settings: WorkflowSettings }>({
      method: 'PUT',
      path: '/api/workflow-settings',
      body: { workflowsPaused: paused },
    });
    return response.settings;
  }
}
